/**
 * @name MinhaContaPage
 * @description Página de gerenciamento da conta do usuário
 * @author Gabriel M. Guimarães | gabrielmg7
 */

import PageContainer from '@/components/layouts/PageContainer';
import { useAlert } from '@/contexts/AlertContext';
import { useLoading } from '@/contexts/LoadingContext';
import useUserContext from '@/contexts/UserContext/useUserContext';
import useThemeContext from '@/themes/hooks/useThemeContext';
import { createHandleChange, dateInputParser, documentParser, trimTextParser } from '@/utils/handleChange';
import { Badge as BadgeIcon, CalendarToday as CalendarIcon, Edit as EditIcon, Email as EmailIcon, Person as PersonIcon, Security as SecurityIcon } from '@mui/icons-material';
import { Avatar, Box, Button, Card, CardContent, Chip, Divider, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';

const MinhaContaPage: React.FC = () => {
     const { theme } = useThemeContext();
     const { userData, updatePassword, updateProfile } = useUserContext();
     const { showError } = useAlert();
     const { loadingStates } = useLoading();
     const isProfileLoading = loadingStates['profile-loading'] || false;

     const [isEditingProfile, setIsEditingProfile] = useState(false);
     const [isChangingPassword, setIsChangingPassword] = useState(false);
     const [profileData, setProfileData] = useState({
          nome: userData?.nome || '',
          email: userData?.email || '',
          documento_numero: userData?.documento_numero || '',
          documento_tipo: userData?.documento_tipo || ('CPF' as 'CPF' | 'CNPJ'),
          data_nascimento: userData?.data_nascimento || '',
     });

     // Handlers usando os utilitários
     const handleNomeChange = createHandleChange((nome: string) => setProfileData((prev) => ({ ...prev, nome })), trimTextParser);

     const handleEmailChange = createHandleChange(
          (email: string) => setProfileData((prev) => ({ ...prev, email })),
          (value: string) => value.trim().toLowerCase(),
     );

     const handleDocumentoChange = createHandleChange(
          (documento_numero: string) => setProfileData((prev) => ({ ...prev, documento_numero })),
          (value: string) => documentParser(profileData.documento_tipo as 'CPF' | 'CNPJ')(value),
     );

     const handleDataNascimentoChange = createHandleChange((data_nascimento: string) => setProfileData((prev) => ({ ...prev, data_nascimento })), dateInputParser);

     const handleTipoDocumentoChange = (tipo: 'CPF' | 'CNPJ') => {
          setProfileData((prev) => ({
               ...prev,
               documento_tipo: tipo,
               documento_numero: '', // Reset do número ao mudar tipo
          }));
     };
     const [passwordData, setPasswordData] = useState({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
     });

     const handleSaveProfile = async () => {
          // Validações básicas
          if (!profileData.nome.trim()) {
               showError('Nome é obrigatório');
               return;
          }

          if (!profileData.email.trim()) {
               showError('Email é obrigatório');
               return;
          }

          // Validação de email
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(profileData.email)) {
               showError('Email inválido');
               return;
          }

          // Validação de documento se fornecido
          if (profileData.documento_numero && profileData.documento_numero.trim()) {
               const docRegex = profileData.documento_tipo === 'CPF' ? /^\d{3}\.\d{3}\.\d{3}-\d{2}$|^\d{11}$/ : /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$|^\d{14}$/;

               if (!docRegex.test(profileData.documento_numero.replace(/\D/g, ''))) {
                    showError(`${profileData.documento_tipo} inválido`);
                    return;
               }
          }

          try {
               await updateProfile({
                    nome: profileData.nome.trim(),
                    email: profileData.email.trim().toLowerCase(),
                    documento_numero: profileData.documento_numero?.replace(/\D/g, '') || undefined,
                    documento_tipo: profileData.documento_tipo,
                    data_nascimento: profileData.data_nascimento || undefined,
               });
               setIsEditingProfile(false);
          } catch (error) {
               // O erro já é tratado dentro da função updateProfile
               console.error('Erro ao salvar perfil:', error);
          }
     };

     const handleChangePassword = async () => {
          if (passwordData.newPassword !== passwordData.confirmPassword) {
               showError('As senhas não coincidem');
               return;
          }

          if (passwordData.newPassword.length < 6) {
               showError('A nova senha deve ter pelo menos 6 caracteres');
               return;
          }

          if (!passwordData.currentPassword) {
               showError('Digite sua senha atual para confirmar a alteração');
               return;
          }

          if (passwordData.currentPassword === passwordData.newPassword) {
               showError('A nova senha deve ser diferente da senha atual');
               return;
          }

          try {
               await updatePassword(passwordData.newPassword);
               // Sucesso já é exibido dentro da função updatePassword
               setIsChangingPassword(false);
               setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
               });
          } catch (error) {
               // Erro já é exibido dentro da função updatePassword
               console.error('Erro ao alterar senha:', error);
          }
     };

     const getInitials = (nome: string) => {
          return nome
               .split(' ')
               .map((word) => word.charAt(0))
               .join('')
               .toUpperCase()
               .slice(0, 2);
     };

     const formatDocument = (documento: string, tipo: string) => {
          if (!documento) return '';

          const clean = documento.replace(/\D/g, '');

          if (tipo === 'CPF' && clean.length === 11) {
               return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
          } else if (tipo === 'CNPJ' && clean.length === 14) {
               return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
          }

          return documento;
     };

     const formatDate = (dateString: string) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toLocaleDateString('pt-BR');
     };

     return (
          <PageContainer
               title='Minha Conta'
               description='Gerencie suas informações pessoais e configurações de conta'
               breadcrumbs={[
                    { label: 'Home', path: '/' },
                    { label: 'Minha Conta', path: '/minha-conta' },
               ]}
          >
               <Grid
                    container
                    spacing={3}
               >
                    {/* Informações do Perfil */}
                    <Grid
                         item
                         xs={12}
                         md={6}
                    >
                         <Card>
                              <CardContent>
                                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <PersonIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                                        <Typography variant='h6'>Informações do Perfil</Typography>
                                   </Box>

                                   {/* Avatar e Nome */}
                                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <Avatar
                                             sx={{
                                                  width: 80,
                                                  height: 80,
                                                  bgcolor: theme.palette.primary.main,
                                                  fontSize: '1.5rem',
                                                  mr: 2,
                                             }}
                                        >
                                             {userData?.nome ? getInitials(userData.nome) : 'U'}
                                        </Avatar>
                                        <Box>
                                             <Typography variant='h6'>{userData?.nome || 'Nome não informado'}</Typography>
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                             >
                                                  {userData?.email || 'Email não informado'}
                                             </Typography>
                                             {userData?.documento_numero && (
                                                  <Typography
                                                       variant='body2'
                                                       color='text.secondary'
                                                  >
                                                       {userData.documento_tipo}: {formatDocument(userData.documento_numero, userData.documento_tipo)}
                                                  </Typography>
                                             )}
                                             <Chip
                                                  label={userData?.ativo ? 'Ativo' : 'Inativo'}
                                                  color={userData?.ativo ? 'success' : 'error'}
                                                  size='small'
                                                  sx={{ mt: 1 }}
                                             />
                                        </Box>
                                   </Box>

                                   <Divider sx={{ mb: 3 }} />

                                   {/* Campos do Perfil */}
                                   <Stack spacing={2}>
                                        <TextField
                                             fullWidth
                                             label='Nome Completo'
                                             value={isEditingProfile ? profileData.nome : userData?.nome || ''}
                                             onChange={isEditingProfile ? handleNomeChange : undefined}
                                             InputProps={{
                                                  readOnly: !isEditingProfile,
                                                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                             }}
                                             inputProps={{
                                                  autoComplete: 'name',
                                                  autoCorrect: 'off',
                                                  autoCapitalize: 'words',
                                             }}
                                             size='small'
                                        />

                                        <TextField
                                             fullWidth
                                             label='Email'
                                             value={isEditingProfile ? profileData.email : userData?.email || ''}
                                             onChange={isEditingProfile ? handleEmailChange : undefined}
                                             InputProps={{
                                                  readOnly: !isEditingProfile,
                                                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                             }}
                                             inputProps={{
                                                  autoComplete: 'off',
                                                  autoCorrect: 'off',
                                                  autoCapitalize: 'off',
                                                  spellCheck: 'false',
                                             }}
                                             size='small'
                                        />

                                        <TextField
                                             fullWidth
                                             select
                                             label='Tipo de Documento'
                                             value={isEditingProfile ? profileData.documento_tipo : userData?.documento_tipo || 'CPF'}
                                             onChange={(e) => (isEditingProfile ? handleTipoDocumentoChange(e.target.value as 'CPF' | 'CNPJ') : undefined)}
                                             InputProps={{
                                                  readOnly: !isEditingProfile,
                                                  startAdornment: <BadgeIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                             }}
                                             size='small'
                                        >
                                             <MenuItem value='CPF'>CPF</MenuItem>
                                             <MenuItem value='CNPJ'>CNPJ</MenuItem>
                                        </TextField>

                                        <TextField
                                             fullWidth
                                             label='Número do Documento'
                                             value={isEditingProfile ? profileData.documento_numero : userData?.documento_numero || ''}
                                             onChange={isEditingProfile ? handleDocumentoChange : undefined}
                                             InputProps={{
                                                  readOnly: !isEditingProfile,
                                                  startAdornment: <BadgeIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                             }}
                                             inputProps={{
                                                  autoComplete: 'off',
                                                  autoCorrect: 'off',
                                                  autoCapitalize: 'off',
                                                  spellCheck: 'false',
                                                  maxLength: profileData.documento_tipo === 'CPF' ? 14 : 18,
                                             }}
                                             size='small'
                                             placeholder={profileData.documento_tipo === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'}
                                        />

                                        <TextField
                                             fullWidth
                                             type='date'
                                             label='Data de Nascimento'
                                             value={isEditingProfile ? profileData.data_nascimento : userData?.data_nascimento || ''}
                                             onChange={isEditingProfile ? handleDataNascimentoChange : undefined}
                                             InputProps={{
                                                  readOnly: !isEditingProfile,
                                                  startAdornment: <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                             }}
                                             InputLabelProps={{
                                                  shrink: true,
                                             }}
                                             inputProps={{
                                                  max: new Date().toISOString().split('T')[0], // Não permite datas futuras
                                             }}
                                             size='small'
                                        />
                                   </Stack>

                                   {/* Botões do Perfil */}
                                   <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                                        {!isEditingProfile ? (
                                             <Button
                                                  variant='outlined'
                                                  startIcon={<EditIcon />}
                                                  onClick={() => setIsEditingProfile(true)}
                                                  fullWidth
                                             >
                                                  Editar Perfil
                                             </Button>
                                        ) : (
                                             <>
                                                  <Button
                                                       variant='contained'
                                                       onClick={handleSaveProfile}
                                                       disabled={isProfileLoading}
                                                       sx={{ flex: 1 }}
                                                  >
                                                       {isProfileLoading ? 'Salvando...' : 'Salvar'}
                                                  </Button>
                                                  <Button
                                                       variant='outlined'
                                                       disabled={isProfileLoading}
                                                       onClick={() => {
                                                            setIsEditingProfile(false);
                                                            setProfileData({
                                                                 nome: userData?.nome || '',
                                                                 email: userData?.email || '',
                                                                 documento_numero: userData?.documento_numero || '',
                                                                 documento_tipo: (userData?.documento_tipo as 'CPF' | 'CNPJ') || 'CPF',
                                                                 data_nascimento: userData?.data_nascimento || '',
                                                            });
                                                       }}
                                                       sx={{ flex: 1 }}
                                                  >
                                                       Cancelar
                                                  </Button>
                                             </>
                                        )}
                                   </Box>
                              </CardContent>
                         </Card>
                    </Grid>

                    {/* Alterar Senha */}
                    <Grid
                         item
                         xs={12}
                         md={6}
                    >
                         <Card>
                              <CardContent>
                                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <SecurityIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                                        <Typography variant='h6'>Segurança da Conta</Typography>
                                   </Box>

                                   {!isChangingPassword ? (
                                        <Box>
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                                  gutterBottom
                                             >
                                                  Mantenha sua conta segura alterando sua senha regularmente.
                                             </Typography>
                                             <Button
                                                  variant='outlined'
                                                  startIcon={<SecurityIcon />}
                                                  onClick={() => setIsChangingPassword(true)}
                                                  sx={{ mt: 2 }}
                                                  fullWidth
                                             >
                                                  Alterar Senha
                                             </Button>
                                        </Box>
                                   ) : (
                                        <Box>
                                             <Stack spacing={2}>
                                                  <TextField
                                                       fullWidth
                                                       type='password'
                                                       label='Senha Atual'
                                                       value={passwordData.currentPassword}
                                                       onChange={(e) =>
                                                            setPasswordData({
                                                                 ...passwordData,
                                                                 currentPassword: e.target.value,
                                                            })
                                                       }
                                                       inputProps={{
                                                            autoComplete: 'current-password',
                                                            autoCorrect: 'off',
                                                            autoCapitalize: 'off',
                                                            spellCheck: 'false',
                                                       }}
                                                       size='small'
                                                  />

                                                  <TextField
                                                       fullWidth
                                                       type='password'
                                                       label='Nova Senha'
                                                       value={passwordData.newPassword}
                                                       onChange={(e) =>
                                                            setPasswordData({
                                                                 ...passwordData,
                                                                 newPassword: e.target.value,
                                                            })
                                                       }
                                                       inputProps={{
                                                            autoComplete: 'new-password',
                                                            autoCorrect: 'off',
                                                            autoCapitalize: 'off',
                                                            spellCheck: 'false',
                                                       }}
                                                       size='small'
                                                  />

                                                  <TextField
                                                       fullWidth
                                                       type='password'
                                                       label='Confirmar Nova Senha'
                                                       value={passwordData.confirmPassword}
                                                       onChange={(e) =>
                                                            setPasswordData({
                                                                 ...passwordData,
                                                                 confirmPassword: e.target.value,
                                                            })
                                                       }
                                                       inputProps={{
                                                            autoComplete: 'new-password',
                                                            autoCorrect: 'off',
                                                            autoCapitalize: 'off',
                                                            spellCheck: 'false',
                                                       }}
                                                       size='small'
                                                  />
                                             </Stack>

                                             <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                                                  <Button
                                                       variant='contained'
                                                       onClick={handleChangePassword}
                                                       disabled={isProfileLoading}
                                                       sx={{ flex: 1 }}
                                                  >
                                                       {isProfileLoading ? 'Alterando...' : 'Alterar Senha'}
                                                  </Button>
                                                  <Button
                                                       variant='outlined'
                                                       disabled={isProfileLoading}
                                                       onClick={() => {
                                                            setIsChangingPassword(false);
                                                            setPasswordData({
                                                                 currentPassword: '',
                                                                 newPassword: '',
                                                                 confirmPassword: '',
                                                            });
                                                       }}
                                                       sx={{ flex: 1 }}
                                                  >
                                                       Cancelar
                                                  </Button>
                                             </Box>
                                        </Box>
                                   )}
                              </CardContent>
                         </Card>

                         {/* Informações da Organização */}
                         <Card sx={{ mt: 3 }}>
                              <CardContent>
                                   <Typography
                                        variant='h6'
                                        gutterBottom
                                   >
                                        Informações da Organização
                                   </Typography>
                                   <Divider sx={{ mb: 2 }} />

                                   <Stack spacing={1}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                             >
                                                  ID da Organização:
                                             </Typography>
                                             <Typography variant='body2'>{userData?.fk_id_organizacao || 'Não informado'}</Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                             >
                                                  Tipo de Usuário:
                                             </Typography>
                                             <Chip
                                                  label={userData?.fk_id_tipo_pessoa === 4 ? 'Responsável' : 'Usuário'}
                                                  color='success'
                                                  size='small'
                                             />
                                        </Box>

                                        {userData?.data_nascimento && (
                                             <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                  <Typography
                                                       variant='body2'
                                                       color='text.secondary'
                                                  >
                                                       Data de Nascimento:
                                                  </Typography>
                                                  <Typography variant='body2'>{formatDate(userData.data_nascimento)}</Typography>
                                             </Box>
                                        )}

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                             >
                                                  Membro desde:
                                             </Typography>
                                             <Typography variant='body2'>{formatDate(userData?.created_at || '')}</Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                             >
                                                  Última atualização:
                                             </Typography>
                                             <Typography variant='body2'>{formatDate(userData?.updated_at || userData?.created_at || '')}</Typography>
                                        </Box>
                                   </Stack>
                              </CardContent>
                         </Card>
                    </Grid>
               </Grid>
          </PageContainer>
     );
};

export { MinhaContaPage };
